<template>
	<div
		ref="wrapper"
		class="modules-popup__wrapper"
		:style="{
			right: `${offset}px`,
		}"
	>
		<div
			v-if="links && links.list.length > 0"
			class="modules-popup__links"
		>
			<helio-icon
				v-if="links.icon"
				:token="links.icon"
				class="modules-popup__links-icon"
				weight="light"
			/>

			<h2
				v-if="links.title"
				class="modules-popup__links-title"
			>
				{{ links.title[$i18n.locale] }}
			</h2>

			<p
				v-if="links.subtitle"
				class="modules-popup__links-subtitle"
			>
				<span v-html="links.subtitle[$i18n.locale]" />
			</p>

			<div
				v-for="(link, index) in links.list"
				:key="index"
				class="modules-popup__link"
			>
				<a
					v-if="link.url && link.name"
					:href="link.url[$i18n.locale]"
					:target="link.target ? link.target : '_self'"
					class="modules-popup__link-name"
				>
					<helio-icon
						class="modules-popup__link-icon"
						token="long-arrow-right"
						weight="light"
					/>

					{{ link.name[$i18n.locale] }}
				</a>
			</div>
		</div>

		<div
			:style="{
				width: modulesContainerWidth,
				height: modulesColumnHeight
			}"
			class="modules-popup__modules"
		>
			<template v-for="(module, index) in modules">
				<a
					:key="index"
					:href="module.url[$i18n.locale]"
					:target="module.target ? module.target : '_self'"
				>
					<div
						:style="{
							margin: `${moduleMargin / 2}px`,
							height: `${moduleSize}px`,
							width: `${moduleSize}px`
						}"
						:class="[
							'modules-popup__module',
							{
								'modules-popup__module--active': isTheActiveUrl(module)
							},
						]"
					>
						<helio-icon
							:token="module.icon"
							class="modules-popup__module-icon"
							weight="light"
						/>

						<p class="modules-popup__module-text">
							{{ module.name[$i18n.locale] }}
						</p>

						<helio-badge
							v-if="module.isNew"
							class="modules-popup__module-badge module-badge-new"
						>
							{{ $t('header.module.new') }}
						</helio-badge>
					</div>
				</a>
			</template>
		</div>
	</div>
</template>

<script>
import _debounce from 'lodash/debounce';
import Icon from '../../../icon/Icon.vue';

export default {
	components: {
		'helio-icon': Icon,
	},

	props: {
		modules: {
			type: Array,
			required: true,
		},

		links: {
			type: Object,
			required: false,
			default: null,
		},

		numberOfModulesPerCol: {
			type: Number,
			required: false,
			default: 3,
		},

		moduleSize: {
			type: Number,
			required: false,
			default: 130,
		},
	},

	data() {
		return {
			moduleMargin: 20,
			wrapperHeight: 0,
			offset: 0,
			activeURL: '',
		};
	},

	computed: {
		modulesContainerWidth() {
			// the width of each column is the combination of the width of a module
			// + its margin, multiplied by the number of columns.
			// this is done by dividing the number of modules by the number of modules per column.
			// the container width is a multiplication of the column width * the number of columns.
			const numberOfColumns = Math.ceil(this.modules.length / this.numberOfModulesPerCol);
			const moduleWidth = this.moduleSize + this.moduleMargin;
			const width = moduleWidth * numberOfColumns;
			return `${width}px`;
		},

		modulesColumnHeight() {
			// calculated the height of the container by calculating the height of each
			// module + its margin. Depending on whether the number of modules to show
			// is smaller or bigger than the number of modules per column we either
			// add up all the module sizes (plus margin) and provide that as the
			// total height, or simply add up the height of the number of
			// modules desired to be in the column.
			const moduleWidth = this.moduleSize + this.moduleMargin;

			if (this.modules.length <= this.numberOfModulesPerCol) {
				return `${moduleWidth * this.modules.length}px`;
			}

			return `${moduleWidth * this.numberOfModulesPerCol}px`;
		},
	},

	mounted() {
		const url = window.location.href;
		const queryIndex = url.indexOf('?');
		const urlWithoutParams = queryIndex > 0 ? url.substring(0, queryIndex) : url;
		this.activeURL = this.cleanUrl(urlWithoutParams);
		this.calculateOffset();
		window.addEventListener('resize', this.debouncedCalculteOffset);
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.debouncedCalculteOffset);
	},

	methods: {
		/* eslint-disable-next-line */
		debouncedEmitChanged: _debounce(function (newValue) {
			this.calculateOffset();
		}, 250),

		isTheActiveUrl(module) {
			const urls = [
				this.cleanUrl(module.url[this.$i18n.locale]),
			];

			if (module.submodules) {
				module.submodules.forEach((submodule) => {
					urls.push(this.cleanUrl(submodule.url[this.$i18n.locale]));
				});
			}

			return urls.includes(this.activeURL);
		},

		calculateOffset() {
			this.$nextTick(() => {
				const { wrapper } = this.$refs;

				if (wrapper) {
					const dimensions = wrapper.getBoundingClientRect();
					const buttonWidth = 150;
					const offset = (dimensions.width - buttonWidth) / 2;
					const spaceLeft = window.innerWidth - dimensions.right;
					this.offset = (offset < spaceLeft ? offset : spaceLeft) * -1;
				}
			});
		},
	},
};
</script>

<style src="./modules-popup.scss" lang="scss" />
